import {
  useMutationAppointmentLateCancelAsMember,
  useMutationAppointmentUpdateAsMember,
  useQueryRulesAsPublic,
} from "@gymflow/api";
import { formatCurrency, rulesHelper } from "@gymflow/helpers";
import { AppointmentDTO } from "@gymflow/types";
import { isOccurrenceInsideCancelationWindow } from "@gymflow/validation";
import { usePortalRoutes } from "apps/portal/src/hooks";
import { useClubSettings } from "apps/portal/src/providers";
import { ToastContext } from "apps/portal/src/providers/ToastProvider/context";
import { RouteFeature } from "apps/portal/src/routes";
import useGymflowModels from "apps/portal/src/store";
import { isAxiosError } from "axios";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { Spinner } from "../../../atoms";
import { ConfirmModal } from "../../../templates";

type HostedPagesProfileBookingAppointmentsCancelModalProps = {
  onCancel: () => void;
  appointmentInfo: AppointmentDTO;
};

export const HostedPagesProfileBookingAppointmentsCancelModal = ({
  onCancel,
  appointmentInfo,
}: HostedPagesProfileBookingAppointmentsCancelModalProps) => {
  const { t } = useTranslation();
  const { api } = useGymflowModels();
  const { createSiteOrEmbedLink } = usePortalRoutes();
  const history = useHistory();
  const { defaultCurrency } = useClubSettings();
  const { notifyDanger, toast } = useContext(ToastContext);

  const { data: appointmentRules, isFetching: isFetchingAppointmentRules } =
    useQueryRulesAsPublic({
      api,
      params: { appointableId: appointmentInfo.appointable.id },
    });

  const { mutateAsync: mutateAsyncAppointmentUpdate } =
    useMutationAppointmentUpdateAsMember({ api });
  const { mutateAsync: mutateAsyncAppointmentLateCancel } =
    useMutationAppointmentLateCancelAsMember({ api });

  const { bookingCancellationRule } =
    rulesHelper.mapRuleGroupToAppointmentRules(appointmentRules);
  const isInsideCancelationWindow = isOccurrenceInsideCancelationWindow(
    appointmentInfo.startDate,
    {
      bookingCancellationRule,
    },
  );
  const isLateCancellation = !isInsideCancelationWindow;

  const isRegular = !isLateCancellation;
  const isLateNoFee =
    isLateCancellation && bookingCancellationRule?.cancellationFee === 0;
  const isLateWithFee =
    isLateCancellation && bookingCancellationRule?.cancellationFee !== 0;

  const onConfirm = async () => {
    if (isRegular) {
      try {
        await mutateAsyncAppointmentUpdate({
          appointmentId: appointmentInfo.id,
          appointment: {
            status: "CANCELLED",
          },
        });
        toast({
          message: t(
            "page.hostedPagesProfileBookingAppointmentsOverview.hostedPagesProfileBookingAppointmentsCancelModal.toast.success",
          ),
          intent: "success",
          type: "small",
        });
        onCancel();
      } catch (e) {
        if (isAxiosError(e)) {
          notifyDanger(e);
        }
      }
    }

    if (isLateNoFee) {
      try {
        await mutateAsyncAppointmentLateCancel({
          appointmentId: appointmentInfo.id,
        });
        toast({
          message: t(
            "page.hostedPagesProfileBookingAppointmentsOverview.hostedPagesProfileBookingAppointmentsCancelModal.toast.success",
          ),
          intent: "success",
          type: "small",
        });
        onCancel();
      } catch (e) {
        if (isAxiosError(e)) {
          notifyDanger(e);
        }
      }
    }

    if (isLateWithFee) {
      history.push(
        createSiteOrEmbedLink(
          RouteFeature.SiteProfileBookingAppointmentLateCancelId.replace(
            ":appointmentId",
            String(appointmentInfo.id),
          ),
        ),
      );
      onCancel();
    }
  };

  return (
    <ConfirmModal
      type="danger"
      title={t(
        "page.hostedPagesProfileBookingAppointmentsOverview.hostedPagesProfileBookingAppointmentsCancelModal.title",
      )}
      onCancel={onCancel}
      cancelText={t(
        "page.hostedPagesProfileBookingAppointmentsOverview.hostedPagesProfileBookingAppointmentsCancelModal.cancelText",
      )}
      confirmText={
        isRegular || isLateNoFee
          ? t(
              "page.hostedPagesProfileBookingAppointmentsOverview.hostedPagesProfileBookingAppointmentsCancelModal.confirmText.cancelBooking",
            )
          : t(
              "page.hostedPagesProfileBookingAppointmentsOverview.hostedPagesProfileBookingAppointmentsCancelModal.confirmText.cancelAndPay",
            )
      }
      onConfirm={async () => await onConfirm()}
      isConfirmDisabled={isFetchingAppointmentRules}
      showCloseButton
    >
      {isFetchingAppointmentRules && <Spinner />}
      {!isFetchingAppointmentRules && appointmentRules && (
        <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
          {isRegular &&
            t(
              "page.hostedPagesProfileBookingAppointmentsOverview.hostedPagesProfileBookingAppointmentsCancelModal.regular.description",
            )}
          {isLateNoFee &&
            t(
              "page.hostedPagesProfileBookingAppointmentsOverview.hostedPagesProfileBookingAppointmentsCancelModal.lateNoFee.description",
            )}
          {isLateWithFee &&
            t(
              "page.hostedPagesProfileBookingAppointmentsOverview.hostedPagesProfileBookingAppointmentsCancelModal.lateWithFee.description",
              {
                price: formatCurrency(
                  bookingCancellationRule?.cancellationFee ?? 0,
                  defaultCurrency,
                ),
              },
            )}
        </div>
      )}
    </ConfirmModal>
  );
};
